import type { CampaignCreationCapabilities } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { Checkbox } from "@/components/ui/checkbox";
import { Empty, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReviewQuickAddTargets } from "../ReviewQuickAddTargets";
import type { QuickTargetValue } from "../review-quick-targets";
import { type Entry, entryIdentity, type GroupLane } from "./target-entries";

export interface MatchFilterItem {
  value: string;
  label: string;
}

export interface QuickAddResult {
  added: number;
  skipped: number;
  error?: string;
}

interface TargetCandidatesSectionProps {
  groupKey: string;
  lane: GroupLane;
  precise: boolean;
  defaultBid: number;
  bidLimits: CampaignCreationCapabilities["bid"] | undefined;
  locked: boolean;
  candidateCount: number;
  entries: Entry[];
  visibleEntries: Entry[];
  search: string;
  matchFilter: string;
  matchItems: MatchFilterItem[];
  categoryNamesLoading: boolean;
  entryLabel(entry: Entry): string;
  matchLabel(match: string): string;
  sourceLabel(entry: Entry): string;
  picked(entry: Entry): boolean;
  onAdd(values: QuickTargetValue[]): QuickAddResult;
  onSearchChange(search: string): void;
  onMatchFilterChange(filter: string): void;
  onEntriesChange(next: Entry[]): void;
  onToggle(entry: Entry, checked: boolean): void;
}

export function TargetCandidatesSection({
  groupKey,
  lane,
  precise,
  defaultBid,
  bidLimits,
  locked,
  candidateCount,
  entries,
  visibleEntries,
  search,
  matchFilter,
  matchItems,
  categoryNamesLoading,
  entryLabel,
  matchLabel,
  sourceLabel,
  picked,
  onAdd,
  onSearchChange,
  onMatchFilterChange,
  onEntriesChange,
  onToggle,
}: TargetCandidatesSectionProps) {
  const { t } = useTranslation("smartCreation");
  return (
    <section
      aria-label={t("review.targetTransfer.candidates")}
      className="flex min-h-0 min-w-0 flex-col gap-2"
    >
      <div className="flex h-8 shrink-0 items-center justify-between gap-2">
        <h3 className="text-xs font-semibold">
          {t("review.targetTransfer.candidates")}{" "}
          <span className="font-normal tabular-nums text-muted-foreground">{candidateCount}</span>
        </h3>
        {!precise && (lane.type === "keyword" || lane.type === "product") ? (
          <ReviewQuickAddTargets
            key={groupKey}
            negative={false}
            targetType={lane.type}
            match={
              lane.match === "phrase"
                ? "phrase"
                : lane.match === "exact"
                  ? "exact"
                  : lane.match === "expanded"
                    ? "expanded"
                    : "broad"
            }
            defaultBid={defaultBid}
            bidLimits={bidLimits}
            disabled={locked}
            onAdd={onAdd}
          />
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Input
          size="sm"
          className="min-w-0 flex-1"
          value={search}
          aria-label={t("review.targetSelection.search")}
          placeholder={t("review.targetSelection.search")}
          onChange={(event) => onSearchChange(event.target.value)}
        />
        <Select
          value={matchFilter}
          items={matchItems}
          onValueChange={(value) => onMatchFilterChange(String(value ?? "all"))}
        >
          <SelectTrigger
            size="sm"
            className="w-32"
            aria-label={t("review.targetSelection.filterMatch")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {matchItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <DataTableFrame
        className="flex min-h-0 flex-1 flex-col rounded-lg"
        viewportClassName="min-h-0 flex-1 overflow-auto"
      >
        <DataTable
          density="compact"
          stickyHeader
          className="w-full table-fixed text-xs [&_thead_th]:h-8 [&_thead_th]:px-2 [&_thead_th]:text-[11px] [&_thead_th]:normal-case [&_thead_th]:tracking-normal [&_tbody_td]:px-2 [&_tbody_td]:py-2 [&_tbody_tr]:h-11"
        >
          <TableHeader>
            <TableRow>
              <TableHead className="w-9">
                <Checkbox
                  checked={visibleEntries.length > 0 && visibleEntries.every(picked)}
                  indeterminate={visibleEntries.some(picked) && !visibleEntries.every(picked)}
                  disabled={locked || !visibleEntries.length}
                  aria-label={t("review.parity.selectCandidates")}
                  onCheckedChange={(checked) =>
                    onEntriesChange(
                      checked
                        ? [...entries, ...visibleEntries.filter((entry) => !picked(entry))]
                        : entries.filter(
                            (entry) =>
                              !visibleEntries.some(
                                (candidate) => entryIdentity(candidate) === entryIdentity(entry),
                              ),
                          ),
                    )
                  }
                />
              </TableHead>
              <TableHead>{t("review.parity.target")}</TableHead>
              <TableHead className="w-22">{t("review.parity.match")}</TableHead>
              <TableHead className="w-35">{t("review.parity.source")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleEntries.map((entry) => (
              <TableRow key={entry.id} className={picked(entry) ? "bg-primary/4" : undefined}>
                <TableCell>
                  <Checkbox
                    checked={picked(entry)}
                    disabled={locked}
                    aria-label={t("review.sheet.useTarget", {
                      name: entryLabel(entry),
                    })}
                    onCheckedChange={(checked) => onToggle(entry, checked)}
                  />
                </TableCell>
                <TableCell className="truncate" title={entryLabel(entry)}>
                  {categoryNamesLoading && entry.original?.type.startsWith("category") ? (
                    <Skeleton className="h-4 w-28" />
                  ) : (
                    entryLabel(entry)
                  )}
                </TableCell>
                <TableCell className="truncate" title={matchLabel(entry.match)}>
                  <span className="text-[11px]">{matchLabel(entry.match)}</span>
                </TableCell>
                <TableCell
                  className="truncate text-xs text-foreground/75"
                  title={sourceLabel(entry)}
                >
                  {sourceLabel(entry)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
        {!visibleEntries.length ? (
          <Empty className="min-h-24 p-4">
            <EmptyHeader>
              <EmptyTitle className="text-xs">{t("review.targetSelection.noResults")}</EmptyTitle>
            </EmptyHeader>
          </Empty>
        ) : null}
      </DataTableFrame>
      <p className="shrink-0 text-[11px] text-muted-foreground">
        {t(precise ? "review.quickAdd.preciseRestriction" : "review.targetTransfer.selectionHint")}
      </p>
    </section>
  );
}
